// src/app/components/sessions/DetalleSesionKine.tsx
// Versión ajustada a las convenciones del repo (semantic tokens).
// Diferencias vs v1-raw:
//   - hex hardcoded en JSX      → tokens Tailwind (bg-card, border-border,
//     text-muted-foreground, text-primary, bg-muted, etc.)
//   - colores internos Recharts → extraídos a CHART_COLORS al tope del archivo
//     (recharts no acepta clases Tailwind en stroke/fill, necesita color strings)
//   - "a la paciente"           → "al paciente" (neutro)
//   - !mb-0                     → mb-0
import { useMemo } from "react"
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  CheckCircle2,
  ChevronRight,
  MessageSquare,
  Minus,
  Settings,
} from "lucide-react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

// ─── Chart colors ─────────────────────────────────────────────────────────
// Centralizado acá porque Recharts recibe color strings, no clases Tailwind.
// Si cambia la paleta de la app, se tocan estos valores. Los hex van alineados
// con --primary (#003B4A), --muted-foreground, --border, y las escalas semánticas.

const CHART_COLORS = {
  primary: "#003B4A",
  gridStroke: "#eef0f2",
  axisLine: "#e2e4e8",
  axisTick: "#8a94a0",
  axisLabel: "#4a5560",
  thresholdLine: "#8a94a0",
  ok: "#16a34a",     // green-600
  warn: "#f59e0b",   // amber-500
  alert: "#ef4444",  // red-500
  tooltipBg: "#ffffff",
  tooltipBorder: "#e2e4e8",
  hoverOverlay: "rgba(0,59,74,0.04)",
}

// ─── Types ────────────────────────────────────────────────────────────────

export interface SesionRep {
  n: number
  angleMax: number
  durationMs: number
  quality: number
  violations: string[]
}

export interface SesionComparacion {
  calidadPctDelta: number
  angleMaxDelta: number
  violacionesDelta: number
}

export interface Sesion {
  id: string
  /** ISO 8601 */
  fecha: string
  paciente: { id: string; nombre: string }
  ejercicio: { id: string; nombre: string }
  duracionSeg: number
  repsTotal: number
  repsObjetivo: number
  seriesCompletas: number
  seriesObjetivo: number
  calidadPct: number
  reps: SesionRep[]
  comparacionAnterior?: SesionComparacion
}

export interface DetalleSesionKineProps {
  sesion: Sesion
  /** Umbral mínimo de ángulo en grados. Default: 45 */
  umbralAngulo?: number
  /** Mapa de slug → label legible para violaciones */
  violationLabels?: Record<string, string>
  onEnviarMensaje: () => void
  onAjustarRutina: () => void
  /** Opcional: sin handler no se muestra el botón (no hay backend todavía). */
  onMarcarRevisada?: () => void
  onIrATodasLasSesiones: () => void
  onVolver?: () => void
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function fmtDur(s: number): string {
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m} min ${String(r).padStart(2, "0")} seg`
}

function qualityFill(q: number): string {
  if (q >= 80) return CHART_COLORS.ok
  if (q >= 50) return CHART_COLORS.warn
  return CHART_COLORS.alert
}

function angleColor(a: number, threshold: number): string {
  if (a >= threshold) return CHART_COLORS.ok
  if (a >= threshold - 5) return CHART_COLORS.warn
  return CHART_COLORS.alert
}

const DEFAULT_VIOLATION_LABELS: Record<string, string> = {
  codo_separado: "Codo separado del cuerpo",
  compensacion_tronco: "Compensación con tronco",
  rom_incompleto: "Rango de movimiento incompleto",
  hombro_elevado: "Hombro elevado",
}

// ─── Main component ──────────────────────────────────────────────────────

export function DetalleSesionKine({
  sesion,
  umbralAngulo = 45,
  violationLabels = DEFAULT_VIOLATION_LABELS,
  onEnviarMensaje,
  onAjustarRutina,
  onMarcarRevisada,
  onIrATodasLasSesiones,
  onVolver,
}: DetalleSesionKineProps) {
  const pctRevisar = sesion.calidadPct < 70
  const fecha = new Date(sesion.fecha)
  const fechaStr = fecha.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
  const horaStr = fecha.toLocaleTimeString("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })

  const repsData = sesion.reps.map((r) => ({
    n: r.n,
    angle: r.angleMax,
    quality: r.quality,
    durationMs: r.durationMs,
    violations: r.violations,
  }))

  const mediaAngulo = useMemo(() => {
    if (!sesion.reps.length) return 0
    const s = sesion.reps.reduce((a, b) => a + b.angleMax, 0)
    return Math.round(s / sesion.reps.length)
  }, [sesion.reps])

  const seriesData = useMemo(() => {
    const n = sesion.seriesObjetivo
    const groupSize = Math.ceil(sesion.reps.length / n)
    return Array.from({ length: n }).map((_, i) => {
      const chunk = sesion.reps.slice(i * groupSize, (i + 1) * groupSize)
      const avg = chunk.length
        ? Math.round(chunk.reduce((a, r) => a + r.quality, 0) / chunk.length)
        : 0
      return { serie: `Serie ${i + 1}`, quality: avg }
    })
  }, [sesion.reps, sesion.seriesObjetivo])

  const violationStats = useMemo(() => {
    const tally: Record<string, number> = {}
    sesion.reps.forEach((r) =>
      r.violations.forEach((v) => (tally[v] = (tally[v] || 0) + 1))
    )
    return Object.entries(tally)
      .map(([slug, count]) => ({
        slug,
        count,
        pct: Math.round((count / sesion.reps.length) * 100),
      }))
      .sort((a, b) => b.count - a.count)
  }, [sesion.reps])

  const allSlugs = Object.keys(violationLabels)
  const perfectSlugs = allSlugs.filter(
    (s) => !violationStats.some((v) => v.slug === s)
  )

  return (
    <div className="min-w-0 overflow-x-hidden">
      {/* Header */}
      <div className="bg-card border-b border-border px-8 py-5">
        <nav className="flex items-center gap-1.5 text-sm text-muted-foreground">
          {onVolver && (
            <>
              <button
                onClick={onVolver}
                className="hover:text-primary font-medium"
              >
                Pacientes
              </button>
              <ChevronRight className="h-3.5 w-3.5" />
            </>
          )}
          <button
            onClick={onIrATodasLasSesiones}
            className="hover:text-primary font-medium"
          >
            {sesion.paciente.nombre}
          </button>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-primary font-medium">
            Sesión del {fechaStr}
          </span>
        </nav>

        <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-primary">
              {sesion.ejercicio.nombre}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {fechaStr} · {horaStr} · {fmtDur(sesion.duracionSeg)}
            </p>
          </div>
          {pctRevisar && (
            <div className="inline-flex items-center gap-1.5 rounded-full bg-red-50 text-red-700 border border-red-200 px-3 py-1.5 text-xs font-semibold">
              <AlertTriangle className="h-4 w-4" />
              Revisar — calidad baja
            </div>
          )}
        </div>
      </div>

      {/* Content grid */}
      <div className="p-8 grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* ── Left column (60%) ── */}
        <div className="xl:col-span-3 space-y-6">
          {/* Cómo le fue */}
          <Card>
            <CardTitle>Cómo le fue</CardTitle>
            <div className="grid grid-cols-3 gap-3 mt-4">
              <BigStat
                value={`${sesion.repsTotal}/${sesion.repsObjetivo}`}
                caption="reps completadas"
                tone={
                  sesion.repsTotal >= sesion.repsObjetivo ? "ok" : "warn"
                }
              />
              <BigStat
                value={`${sesion.calidadPct}%`}
                caption="calidad promedio"
                tone={
                  sesion.calidadPct >= 80
                    ? "ok"
                    : sesion.calidadPct >= 50
                      ? "warn"
                      : "alert"
                }
              />
              <BigStat
                value={`${sesion.seriesCompletas}/${sesion.seriesObjetivo}`}
                caption="series completas"
                tone={
                  sesion.seriesCompletas >= sesion.seriesObjetivo
                    ? "ok"
                    : "warn"
                }
              />
            </div>
          </Card>

          {/* Angle per rep chart */}
          <Card>
            <div className="flex items-baseline justify-between">
              <CardTitle>Ángulo máximo por repetición</CardTitle>
              <span className="text-xs font-semibold text-muted-foreground">
                {sesion.reps.length} reps
              </span>
            </div>
            <div className="h-[260px] mt-4 -ml-2">
              <ResponsiveContainer>
                <LineChart
                  data={repsData}
                  margin={{ top: 8, right: 16, bottom: 20, left: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={CHART_COLORS.gridStroke}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="n"
                    tick={{ fontSize: 11, fill: CHART_COLORS.axisTick }}
                    axisLine={{ stroke: CHART_COLORS.axisLine }}
                    tickLine={false}
                    label={{
                      value: "Repetición",
                      position: "insideBottom",
                      offset: -8,
                      fill: CHART_COLORS.axisTick,
                      fontSize: 11,
                    }}
                  />
                  <YAxis
                    domain={["dataMin - 10", "dataMax + 10"]}
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: CHART_COLORS.axisTick }}
                    axisLine={false}
                    tickLine={false}
                    label={{
                      value: "Ángulo (°)",
                      angle: -90,
                      position: "insideLeft",
                      offset: 15,
                      fill: CHART_COLORS.axisTick,
                      fontSize: 11,
                    }}
                  />
                  <Tooltip
                    content={
                      <AngleTooltip violationLabels={violationLabels} />
                    }
                  />
                  <ReferenceLine
                    y={umbralAngulo}
                    stroke={CHART_COLORS.thresholdLine}
                    strokeDasharray="4 4"
                    label={{
                      value: `Umbral ${umbralAngulo}°`,
                      position: "insideTopRight",
                      fill: CHART_COLORS.axisLabel,
                      fontSize: 10,
                      fontWeight: 600,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="angle"
                    stroke={CHART_COLORS.primary}
                    strokeWidth={2}
                    dot={(props: any) => (
                      <ColoredDot {...props} threshold={umbralAngulo} />
                    )}
                    activeDot={false}
                    isAnimationActive={true}
                    animationDuration={900}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="text-xs text-muted-foreground mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
              <span>
                Umbral mínimo:{" "}
                <strong className="text-primary">{umbralAngulo}°</strong>
              </span>
              <span>
                Media del paciente:{" "}
                <strong className="text-primary">{mediaAngulo}°</strong>
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-green-500" />
                Pasó
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-amber-500" />
                Cerca
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-red-500" />
                No llegó
              </span>
            </p>
          </Card>

          {/* Calidad por serie */}
          <Card>
            <CardTitle>Distribución de calidad por serie</CardTitle>
            <div className="h-[200px] mt-4 -ml-2">
              <ResponsiveContainer>
                <BarChart
                  data={seriesData}
                  margin={{ top: 8, right: 16, bottom: 8, left: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={CHART_COLORS.gridStroke}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="serie"
                    tick={{
                      fontSize: 12,
                      fill: CHART_COLORS.axisLabel,
                      fontWeight: 600,
                    }}
                    axisLine={{ stroke: CHART_COLORS.axisLine }}
                    tickLine={false}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 11, fill: CHART_COLORS.axisTick }}
                    axisLine={false}
                    tickLine={false}
                    label={{
                      value: "Calidad (%)",
                      angle: -90,
                      position: "insideLeft",
                      offset: 15,
                      fill: CHART_COLORS.axisTick,
                      fontSize: 11,
                    }}
                  />
                  <Tooltip
                    content={<SerieTooltip />}
                    cursor={{ fill: CHART_COLORS.hoverOverlay }}
                  />
                  <ReferenceLine
                    y={80}
                    stroke={CHART_COLORS.ok}
                    strokeDasharray="4 4"
                    opacity={0.5}
                  />
                  <ReferenceLine
                    y={50}
                    stroke={CHART_COLORS.warn}
                    strokeDasharray="4 4"
                    opacity={0.5}
                  />
                  <Bar dataKey="quality" radius={[8, 8, 0, 0]}>
                    {seriesData.map((d, i) => (
                      <Cell key={i} fill={qualityFill(d.quality)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* ── Right column (40%) ── */}
        <div className="xl:col-span-2 space-y-6">
          {/* Observaciones automáticas */}
          <Card>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center">
                <AlertTriangle className="h-4 w-4" strokeWidth={2.3} />
              </div>
              <CardTitle className="mb-0">Observaciones automáticas</CardTitle>
            </div>
            <ul className="mt-4 space-y-2.5">
              {violationStats.map((v) => (
                <li
                  key={v.slug}
                  className="flex items-start gap-3 p-3 rounded-xl bg-amber-50/60 border border-amber-100"
                >
                  <div className="h-2 w-2 rounded-full bg-amber-500 mt-2 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-primary">
                      {violationLabels[v.slug] ?? v.slug}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {v.count} reps ({v.pct}%)
                    </p>
                  </div>
                </li>
              ))}
              {perfectSlugs.slice(0, 2).map((slug) => (
                <li
                  key={slug}
                  className="flex items-start gap-3 p-3 rounded-xl bg-green-50/60 border border-green-100"
                >
                  <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-primary">
                      {violationLabels[slug] ?? slug}: correcto en 100%
                    </p>
                  </div>
                </li>
              ))}
              {violationStats.length === 0 && perfectSlugs.length === 0 && (
                <li className="text-sm text-muted-foreground text-center py-4">
                  Sin observaciones.
                </li>
              )}
            </ul>
          </Card>

          {/* Comparación */}
          {sesion.comparacionAnterior && (
            <Card>
              <CardTitle>Vs. sesión anterior</CardTitle>
              <div className="mt-3 divide-y divide-border">
                <DeltaRow
                  label="Calidad"
                  value={`${sesion.calidadPct}%`}
                  delta={sesion.comparacionAnterior.calidadPctDelta}
                  suffix="%"
                  positiveIsUp
                />
                <DeltaRow
                  label="Ángulo máximo"
                  value={`${mediaAngulo}°`}
                  delta={sesion.comparacionAnterior.angleMaxDelta}
                  suffix="°"
                  positiveIsUp
                />
                <DeltaRow
                  label="Violaciones"
                  value={violationStats.reduce((a, v) => a + v.count, 0)}
                  delta={sesion.comparacionAnterior.violacionesDelta}
                  suffix=""
                  positiveIsUp={false}
                />
              </div>
            </Card>
          )}

          {/* Acciones */}
          <Card>
            <CardTitle>Acciones</CardTitle>
            <div className="mt-4 flex flex-col gap-2">
              <button
                onClick={onEnviarMensaje}
                className="h-11 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-sm inline-flex items-center justify-center gap-2 shadow-sm transition-colors"
              >
                <MessageSquare className="h-4 w-4" strokeWidth={2.3} />
                Enviar mensaje al paciente
              </button>
              <button
                onClick={onAjustarRutina}
                className="h-11 rounded-full bg-muted hover:bg-border text-primary font-semibold text-sm inline-flex items-center justify-center gap-2 transition-colors"
              >
                <Settings className="h-4 w-4" strokeWidth={2.3} />
                Ajustar rutina
              </button>
              {onMarcarRevisada && (
              <button
                onClick={onMarcarRevisada}
                className="h-11 rounded-full border border-border text-primary font-semibold text-sm inline-flex items-center justify-center gap-2 hover:bg-muted transition-colors"
              >
                <CheckCircle2 className="h-4 w-4" strokeWidth={2.3} />
                Marcar como revisada
              </button>
              )}
              <button
                onClick={onIrATodasLasSesiones}
                className="mt-1 text-xs text-primary hover:underline font-semibold text-center"
              >
                Ver todas las sesiones del paciente →
              </button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}

// ─── Building blocks ──────────────────────────────────────────────────────

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm p-6">
      {children}
    </div>
  )
}

function CardTitle({
  children,
  className = "",
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <h2 className={`text-base font-bold text-primary mb-1 ${className}`}>
      {children}
    </h2>
  )
}

function BigStat({
  value,
  caption,
  tone,
}: {
  value: string
  caption: string
  tone: "ok" | "warn" | "alert"
}) {
  const toneClass = {
    ok: "bg-green-50 text-green-700 border-green-100",
    warn: "bg-amber-50 text-amber-700 border-amber-100",
    alert: "bg-red-50 text-red-700 border-red-100",
  }[tone]
  return (
    <div className={`rounded-2xl border ${toneClass} p-4 text-center`}>
      <p className="text-2xl font-bold tabular-nums leading-none">{value}</p>
      <p className="text-[11px] font-semibold mt-2 opacity-80 leading-tight">
        {caption}
      </p>
    </div>
  )
}

function DeltaRow({
  label,
  value,
  delta,
  suffix,
  positiveIsUp,
}: {
  label: string
  value: React.ReactNode
  delta: number
  suffix: string
  positiveIsUp: boolean
}) {
  const good = positiveIsUp ? delta > 0 : delta < 0
  const bad = positiveIsUp ? delta < 0 : delta > 0
  const color = good ? "text-green-600" : bad ? "text-red-600" : "text-muted-foreground"
  const ArrowIcon = delta > 0 ? ArrowUpRight : delta < 0 ? ArrowDownRight : Minus
  return (
    <div className="flex items-center justify-between py-2.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-base font-bold text-primary tabular-nums">
          {value}
        </span>
        <span
          className={`inline-flex items-center gap-0.5 text-xs font-semibold ${color}`}
        >
          <ArrowIcon className="h-3.5 w-3.5" strokeWidth={2.5} />
          {Math.abs(delta)}
          {suffix}
        </span>
      </div>
    </div>
  )
}

function ColoredDot(props: any) {
  const { cx, cy, payload, threshold } = props
  if (cx == null || cy == null) return null
  const color = angleColor(payload.angle, threshold)
  return (
    <circle cx={cx} cy={cy} r={4} fill={color} stroke="#fff" strokeWidth={1.5} />
  )
}

function AngleTooltip({
  active,
  payload,
  violationLabels,
}: {
  active?: boolean
  payload?: any[]
  violationLabels: Record<string, string>
}) {
  if (!active || !payload?.length) return null
  const p = payload[0].payload
  return (
    <div className="bg-card rounded-xl shadow-lg border border-border px-3 py-2 text-xs">
      <p className="font-bold text-primary">Rep #{p.n}</p>
      <p className="text-muted-foreground">
        Ángulo: <strong>{p.angle}°</strong>
      </p>
      <p className="text-muted-foreground">
        Duración: <strong>{(p.durationMs / 1000).toFixed(1)}s</strong>
      </p>
      <p className="text-muted-foreground">
        Calidad: <strong>{p.quality}%</strong>
      </p>
      {p.violations.length > 0 && (
        <p className="text-amber-600 text-[11px] mt-1">
          {p.violations
            .map((v: string) => violationLabels[v] ?? v)
            .join(", ")}
        </p>
      )}
    </div>
  )
}

function SerieTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: any[]
}) {
  if (!active || !payload?.length) return null
  const p = payload[0].payload
  return (
    <div className="bg-card rounded-xl shadow-lg border border-border px-3 py-2 text-xs">
      <p className="font-bold text-primary">{p.serie}</p>
      <p className="text-muted-foreground">
        Calidad: <strong>{p.quality}%</strong>
      </p>
    </div>
  )
}

export default DetalleSesionKine
